/**
 * <TutorSessionRibbon> — la séance de l'élève regardé, de l'ouverture de la salle à maintenant, d'un coup d'œil
 * (molecule).
 *
 * En tête de la colonne du champion : trois couches sur une même règle de temps.
 *  - en bas, les plages **Dialogue** et **Concentré** (les couleurs de la bascule du son) ;
 *  - au milieu, les **activités** en blocs, l'activité en cours ouverte vers maintenant ;
 *  - au-dessus, les **événements** en points (main levée, rendu, échange avec le champion, activité envoyée) ; leur
 *    libellé se lit au survol et au lecteur d'écran.
 *
 * Tout ce qu'il montre est un signal du live que le navigateur a vu depuis l'ouverture de la salle (arbitrage 4 :
 * l'historique ne vit que dans le navigateur) ; l'écran le construit (`tutorSessionRibbon`).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutorRibbonEventKind, type UITutorSessionRibbon } from "../../../ports/primary/ui/types/tutorLive"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { LiveDot } from "../../atoms/LiveDot"
import { Text } from "../../atoms/typography/Text"

export type { UITutorRibbonEventKind }

export type TutorSessionRibbonProps = UITutorSessionRibbon & {
    /** Le point choisi. */
    testID?: string
}

const EVENT_FACE: Record<UITutorRibbonEventKind, { color: SemanticColorKey; icon: IconName }> = {
    hand: { color: "warning", icon: "hand-paper" },
    done: { color: "success", icon: "check" },
    helper: { color: "info", icon: "comment-dots" },
    error: { color: "danger", icon: "times" },
    sent: { color: "role", icon: "telegram-plane" },
}

const TEXTS = {
    session: (minutes: number) => `Séance · ${minutes} min`,
    dialogue: "Dialogue",
    focused: "Concentré",
}

/** Une graduation toutes les 10 minutes ; toutes les 5 sous 20 minutes, pour qu'une séance courte ait sa règle. */
const TICK_EVERY = 10
const DOT = 18

export function TutorSessionRibbon(props: TutorSessionRibbonProps) {
    const { now, modes, activities, events, testID } = props
    const pct = (minute: number) => `${Math.max(0, Math.min(100, (minute / now) * 100))}%` as const
    const width = (from: number, to: number) => `${Math.max(0, ((to - from) / now) * 100)}%` as const
    const every = now < 20 ? TICK_EVERY / 2 : TICK_EVERY
    const ticks = Array.from({ length: Math.floor(now / every) + 1 }, (_, index) => index * every)

    return (
        <Stack gap="2" testID={testID}>
            <Row align="center" justify="between">
                <Row gap="2" align="center">
                    <LiveDot />
                    <Text size="text-xs" weight="bold" color="text-muted" uppercase>
                        {TEXTS.session(Math.max(1, Math.round(now)))}
                    </Text>
                </Row>
                <Row gap="3" align="center">
                    <Legend color="success" label={TEXTS.dialogue} />
                    <Legend color="purple" label={TEXTS.focused} />
                </Row>
            </Row>

            <View style={styles.track}>
                {/* Les événements, au-dessus. */}
                <View style={styles.events}>
                    {events.map((event) => {
                        const face = EVENT_FACE[event.kind]
                        return (
                            <View key={event.id} accessibilityLabel={event.label} style={styles.dotAt(pct(event.at))}>
                                <View style={styles.dot(face.color)}>
                                    <Icon name={face.icon} size={9} color="white" />
                                </View>
                            </View>
                        )
                    })}
                </View>

                {/* Les activités, au milieu. */}
                <View style={styles.activities}>
                    {activities.map((activity) => (
                        <View
                            key={activity.id}
                            style={styles.activity(
                                pct(activity.from),
                                width(activity.from, activity.to),
                                activity.current === true
                            )}
                        >
                            <Text
                                size="text-xxs"
                                weight="semibold"
                                color={activity.current === true ? "text-on-role" : "text-strong"}
                                numberOfLines={1}
                            >
                                {activity.label}
                            </Text>
                        </View>
                    ))}
                </View>

                {/* Les plages du son, en bas. */}
                <View style={styles.modes}>
                    {modes.map((mode, index) => (
                        <View key={index} style={styles.mode(pct(mode.from), width(mode.from, mode.to), mode.mode)} />
                    ))}
                </View>
            </View>

            <View style={styles.ticks}>
                {ticks.map((tick) => (
                    <Text key={tick} size="text-xxs" color="text-muted" style={styles.tickAt(pct(tick))}>
                        {tick === 0 ? "0" : `${tick}′`}
                    </Text>
                ))}
            </View>
        </Stack>
    )
}

function Legend(props: { color: "success" | "purple"; label: string }) {
    return (
        <Row gap="1" align="center">
            <View style={styles.legend(props.color)} />
            <Text size="text-xxs" color="text-muted">
                {props.label}
            </Text>
        </Row>
    )
}

const TRACK_HEIGHT = 58

const styles = StyleSheet.create((theme) => ({
    track: { height: TRACK_HEIGHT, position: "relative" as const },
    events: { position: "absolute" as const, left: 0, right: 0, top: 0, height: DOT },
    dotAt: (left: `${number}%`) => ({
        position: "absolute" as const,
        left,
        top: 0,
        marginLeft: -DOT / 2,
    }),
    dot: (color: SemanticColorKey) => ({
        width: DOT,
        height: DOT,
        borderRadius: DOT / 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors[color],
        borderWidth: 2,
        borderColor: theme.colors.surface,
    }),
    activities: { position: "absolute" as const, left: 0, right: 0, top: DOT + 4, height: 20 },
    activity: (left: `${number}%`, width: `${number}%`, current: boolean) => ({
        position: "absolute" as const,
        left,
        width,
        height: 20,
        paddingHorizontal: 6,
        justifyContent: "center" as const,
        borderRadius: theme.radius.sm,
        backgroundColor: current ? theme.colors.role : theme.colors["surface-subtle"],
        borderWidth: 1,
        borderColor: current ? theme.colors.role : theme.colors["border-subtle"],
        overflow: "hidden" as const,
    }),
    modes: {
        position: "absolute" as const,
        left: 0,
        right: 0,
        bottom: 0,
        height: 8,
        borderRadius: theme.radius.full,
        overflow: "hidden" as const,
        backgroundColor: theme.colors["surface-subtle"],
    },
    mode: (left: `${number}%`, width: `${number}%`, mode: "dialogue" | "focused") => ({
        position: "absolute" as const,
        left,
        width,
        top: 0,
        bottom: 0,
        backgroundColor: mode === "dialogue" ? theme.colors.success : theme.colors.purple,
    }),
    ticks: { height: 14, position: "relative" as const },
    tickAt: (left: `${number}%`) => ({ position: "absolute" as const, left, marginLeft: -4 }),
    legend: (color: "success" | "purple") => ({
        width: 10,
        height: 6,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors[color],
    }),
}))
